<template>
	<div tabindex="0" @keyup.esc="closeAdd()" class="hide-outline">
		<DataTable ref="dataTable"
			title="Anställda"
			:units="['anställd', 'anställda', 'Ny anställd']"
			:orderBys="orderBys"
			:queryParams="getQueryParams"
			:headers="getHeaders"
			:hide-add="!$isAllowedTo('users_edit')"
			@on-add-click="showAdd = true"
			@on-row-click="editUser"
			service="user"
			method="getUsersIncludingExternal"
		>
			<template #item.link="{item}">
				<div class="list-button" @click.stop="handleLinkUserClick(item)" v-if="$isAllowedTo('users_link_within_own_root')">
					<i class="fas" :class="[!isItemExternal(item) ? 'fa-link' : 'fa-unlink']"></i>
				</div>
			</template>
			<template #item.edit="{item}">
				<div class="list-button" @click.stop="editUser(item)" v-if="!isItemExternal(item)">
					<i class="fas fa-edit"></i>
				</div>
			</template>
			<template #item.delete="{item}">
				<div class="list-button" @click.stop="askToDelete(item)"  v-if="!isItemExternal(item)">
					<i class="fas fa-trash-alt"></i>
				</div>
			</template>
		</DataTable>

		<AddUser
			v-if="showAdd"
			:user="selectedUser"
			@cancel="closeAdd"
			@on-update="$refs.dataTable.fetchData()"
		/>
		<DeleteModal
			v-if="showDelete"
			@confirm="deleteSelected"
			@cancel="cancelDelete"
			:message="deleteMessage"
			:error="userDeleteError"
		/>

		<ui-dialog
			title="Koppla anställd till annan kund"
			class="link-user-modal"
			v-if="displayUserLinkModal"
			@close="handleCloseLinkUserModal"
		>
			<ui-progress v-if="userLinkingIsLoading" />

			<div class="columns is-multiline">
				<div class="select-container column is-half" v-if="$isAllowedTo('users_link')">
					<label class="label">Företag</label>
					<v-select
						class="panel-select"
						placeholder="Välj företag"
						v-model="selectedCompany"
						:options="companies"
						:showLabels="false"
						:internalSearch="false"
						label="name"
						@search-change="handleCompanySearchChange"
						@select="handleCompanySelection"
					>
						<template #afterList>
							<div class="is-flex is-justify-content-center" v-if="!disableCompanyFetchButton && companies.length > 0">
								<ui-btn class="is-small" @click="fetchCompanies">
									Ladda fler
								</ui-btn>
							</div>
						</template>
						<p slot="noResult">Inga resultat</p>
					</v-select>
				</div>

				<div class="select-container column is-half">
					<label class="label">Kund</label>
					<v-select
						class="panel-select"
						placeholder="Välj kund"
						v-model="selectedCustomer"
						:options="comCustomers"
						:showLabels="false"
						:internalSearch="false"
						label="name"
						@search-change="handleCustomerSearchChange"
						@select="handleCustomerSelection"
					>
						<template #afterList v-if="$isAllowedTo('users_link_within_company')">
							<div class="is-flex is-justify-content-center" v-if="!disableCustomerFetchButton && customers.length > 0">
								<ui-btn class="is-small" @click="fetchCustomers">
									Ladda fler
								</ui-btn>
							</div>
						</template>
						<p slot="noOptions" 
							v-if="!selectedCompany && $isAllowedTo('users_link')"
						>
							Välj ett företag för att se en lista av kunder
						</p>
						<p slot="noOptions" v-if="selectedCompany">Finns inga kunder på valt företag</p>
						<p 
							slot="noResult" 
							v-if="!$isAllowedTo('users_link_within_company')"
						>
							Hittade inga kunder. Prova ändra sökterm
						</p>
					</v-select>
				</div>

				<div class="select-container mt-4 column is-full">
					<label class="label">Lägg till Roller</label>
					<v-select
						class="panel-select"
						placeholder="Välj roller"
						v-model="selectedRoles"
						:multiple="true"
						:hideSelected="true"
						:closeOnSelect="false"
						:options="roles"
						:showLabels="false"
						:internalSearch="false"
						tagPosition="bottom"
						label="name"
					>
						<template #tag>
							<div></div>
						</template>

						<template #afterList>
							<div class="is-flex is-justify-content-center" v-if="!disableRoleFetchButton && roles.length > 0">
								<ui-btn class="is-small" @click="fetchRoles">
									Ladda fler
								</ui-btn>
							</div>
						</template>
						<p slot="noOptions" v-if="!selectedCustomer">Välj en kund för att se en lista av roller</p>
						<p slot="noOptions" v-if="selectedCustomer">Finns inga roller på vald kund</p>
					</v-select>
				</div>

				<div class="column is-full">
					<div class="form__list">
						<div class="form__list-item" v-for="role in selectedRoles" :key="role.id">
							{{ role.name }}
							<span class="icon" @click="removeSelectedRole(role)">
								<i class="fas fa-times"/>
							</span>
						</div>
					</div>
				</div>
			</div>

			<template #actions>
				<div class="is-flex is-flex-direction-row-reverse">
					<ui-btn class="ml-2 has-text-white" outlined @click="handleCloseLinkUserModal">Avbryt</ui-btn>
					<ui-btn info @click="handleSaveLinkUser" :disabled="!selectedCustomer">Spara</ui-btn>
				</div>
			</template>
		</ui-dialog>

		<DeleteModal 
			v-if="displayDeleteLinkedUserModal"
			:message="`Vill du ta bort kopplingen för ${userToLink.firstName}${` ${userToLink.lastName}`}?`"
			@confirm="handleConfirmDeleteLinkedUser"
			@cancel="handleCloseDeleteLinkedUserModal"
		/>
	</div>
</template>

<script>
import { mapGetters } from 'vuex';
import AddUser from './modals/AddUser';
import DeleteModal from '@/components/DeleteModal';

export default {
	components: {
		AddUser,
		DeleteModal
	},

	data: () => ({
		showAdd: false,
		showDelete: false,
		selectedUser: {},
		orderBys: [
			{ name: 'Förnamn', value: 'FirstName' },
			{ name: 'Efternamn', value: 'LastName' },
			{ name: 'Skapad', value: 'Created' }
		],

		userDeleteError: '',

		userLinkingIsLoading: false,
		userLinkingError: '',
		userToLink: null,
		displayUserLinkModal: false,
		displayDeleteLinkedUserModal: false,

		companies: [],
		selectedCompany: null,
		companySearchTimeout: null,
		disableCompanyFetchButton: false,
		companyQueryParams: {
			pageSize: 10,
			pageNumber: 1,
			name: ''
		},

		customers: [],
		selectedCustomer: null,
		disableCustomerFetchButton: false,
		customerLocalSearchTerm: '',
		customerSearchTimeout: null,
		customerQueryParams: {
			pageSize: 10,
			pageNumber: 1,
			companyId: null,
			name: ''
		},

		roles: [],
		selectedRoles: [],
		disableRoleFetchButton: false,
		roleQueryParams: {
			pageSize: 10,
			pageNumber: 1,
			customerId: null
		}
	}),

	computed: {
		...mapGetters('appStore', ['isMobile']),
		...mapGetters('user', ['getUser','getActiveCustomer', 'getActiveCompany']),

		getHeaders() {
			return [
				{
					text: 'Namn',
					transform: item => `${item.firstName} ${item.lastName}`
				},
				!this.isMobile && {
					text: 'Kund',
					value: 'customer.name'
				},
				!this.isMobile && {
					text: 'Email',
					value: 'email'
				},
				!this.isMobile && {
					text: 'Telefonnummer',
					value: 'phoneNumber'
				},
				this.$isAllowedTo('users_link_within_own_root') && {
					text: '',
					value: 'link',
					className: 'list-button'
				},
				this.$isAllowedTo('users_edit') && {
					text: '',
					value: 'edit',
					className: 'list-button'
				},
				this.$isAllowedTo('users_edit') && {
					text: '',
					value: 'delete',
					className: 'list-button'
				}
			].filter(Boolean);
		},

		getQueryParams() {
			return {
				customerId: this.getActiveCustomer?.id,
				orderBy: 'LastName'
			};
		},

		deleteMessage() {
			return this.selectedUser.firstName
				? `Är du säker på att du vill ta bort ${this.selectedUser.firstName} ${this.selectedUser.lastName}?`
				: '';
		},

		comCustomers() {
			return this.customers
				.filter(x => x.id !== this.getActiveCustomer.id && 
					(!this.customerLocalSearchTerm || x.name.includes(this.customerLocalSearchTerm)));
		}
	},

	methods: {
		toggleAdd() {
			if (!this.selectedUser.firstName) {
				this.showAdd = !this.showAdd;
			}

			this.selectedUser = {};
		},

		closeAdd() {
			if (window.getSelection().toString() == '') {
				this.selectedUser = {};
				this.showAdd = false;
			}

			if (this.showDelete) {
				this.showDelete = false;
				this.selectedUser = {};
			}
		},

		editUser(user) {
			if (!this.$isAllowedTo('users_edit')) return;

			this.selectedUser = user;
			this.showAdd = true;
		},

		askToDelete(user) {
			this.showAdd = false;
			Object.assign(this.selectedUser, user);
			this.showDelete = true;
		},

		async handleLinkUserClick(user) {
			this.userToLink = user;

			if (user.customerId !== this.getActiveCustomer.id)
				return this.displayDeleteLinkedUserModal = true;

			if (this.$isAllowedTo('users_link'))
				await this.fetchCompanies();
			
			this.displayUserLinkModal = true;
			this.selectedCompany = this.getActiveCompany;
			this.customerQueryParams.companyId = this.getActiveCompany.id;
			await this.fetchCustomers();
		},

		handleCloseLinkUserModal() {
			this.userToLink = null;
			this.selectedCompany = null;
			this.selectedCustomer = null;
			this.selectedRoles = [];
			this.resetCompanyQueryParams();
			this.resetCustomerQueryParams();
			this.resetRoleQueryParams();
			this.roles = [];
			this.companies = [];
			this.customers = [];
			this.displayUserLinkModal = false;
		},

		cancelDelete() {
			this.userDeleteError = '';
			this.showDelete = false;
			this.selectedUser = {};
		},

		async deleteSelected() {
			try {
				await this.$services.user.delete(this.selectedUser.id);
				this.showDelete = false;
				this.selectedUser = {};
				this.$refs.dataTable.fetchData();
			} catch (error) {
				this.userDeleteError = error;
			}
		},

		handleCompanySearchChange(input) {
			if (this.companySearchTimeout !== null)
				clearTimeout(this.companySearchTimeout);

			this.companySearchTimeout = setTimeout(
				() => {
					this.resetCompanyQueryParams();
					this.companyQueryParams.name = input;

					this.fetchCompanies();
				}, 500);
		},

		handleCustomerSearchChange(input) {
			if (!this.$isAllowedTo('users_link_within_company'))
				return this.customerLocalSearchTerm = input;

			if (this.customerSearchTimeout !== null)
				clearTimeout(this.customerSearchTimeout);

			this.customerSearchTimeout = setTimeout(
				() => {
					this.resetCustomerQueryParams();
					this.customerQueryParams.name = input;

					this.fetchCustomers();
				}, 500);
		},

		resetCompanyQueryParams() {
			this.disableCompanyFetchButton = false;
			this.companyQueryParams = {
				pageNumber: 1,
				pageSize: 10,
				name: ''
			};
		},

		resetCustomerQueryParams() {
			this.disableCustomerFetchButton = false;
			this.customerQueryParams = {
				pageNumber: 1,
				pageSize: 10,
				companyId: this.customerQueryParams.companyId,
				name: ''
			};
		},

		resetRoleQueryParams() {
			this.disableRoleFetchButton = false;
			this.roleQueryParams = {
				pageNumber: 1,
				pageSize: 10,
				companyId: null
			};
		},

		async fetchCompanies() {
			try {
				if (this.companyQueryParams.pageNumber === 1 && this.companies.length > 0) {
					this.companies = [];
				}

				let companies = await this.$services.company.list(this.companyQueryParams, {
					loading: val => this.userLinkingIsLoading = val
				});

				this.companies = [...this.companies, ...companies];
				this.companyQueryParams.pageNumber = this.companyQueryParams.pageNumber + 1;

				if (companies.length < this.companyQueryParams.pageSize) {
					this.disableCompanyFetchButton = true;
				}

			} catch(error) {
				this.userLinkingError = error.message;
			}
		},

		async fetchCustomers() {
			try {
				if (this.$isAllowedTo('users_link_within_company')) {
					if (this.customerQueryParams.pageNumber === 1 && this.customers.length > 0) {
						this.customers = [];
					}
	
					let customers = await this.$services.customer.list(this.customerQueryParams, {
						loading: val => this.userLinkingIsLoading = val
					});
	
					this.customers = [...this.customers, ...customers];
					this.customerQueryParams.pageNumber = this.companyQueryParams.pageNumber + 1;
	
					if (customers.length < this.customerQueryParams.pageSize) {
						this.disableCustomerFetchButton = true;
					}
				} else {
					let rootNode = null;

					if (this.$isAllowedTo('tree_see_own_root')) {
						rootNode = await this.$services.customer
							.getRootCustomer({
								customerId: this.getActiveCustomer.id, 
								companyId: this.getActiveCompany.id 
							}, {
								loading: val => this.isPending = val
							});
					}

					let customers = await this.$services.customer.getTree({
						companyNode: this.getActiveCompany.id,
						parentNode: rootNode ? rootNode.id : this.getUser.customerId
					}, {
						loading: val => this.userLinkingIsLoading = val
					});

					let flattenedTree = this.getCustomersRecursively(customers);

					this.customers = flattenedTree;
				}

			} catch(error) {
				this.userLinkingError = error.message;
			}
		},

		async fetchRoles() {
			try {
				let roles = await this.$services.role.list(this.roleQueryParams, {
					loading: val => this.userLinkingIsLoading = val
				});

				this.roles = [...this.roles, ...roles];
				this.roleQueryParams.pageNumber = this.roleQueryParams.pageNumber + 1;

				if (roles.length < this.customerQueryParams.pageSize) {
					this.disableRoleFetchButton = true;
				}

			} catch (error) {
				this.userLinkingError = error.message;
			}
		},

		async handleCompanySelection(company) {
			this.resetCustomerQueryParams();
			this.customerQueryParams.companyId = company.id;
			this.selectedCustomer = null;
			this.selectedRoles = [];
			this.roles = [];

			await this.fetchCustomers();
		},

		async handleCustomerSelection(customer) {
			this.resetRoleQueryParams();
			this.roleQueryParams.customerId = customer.id;
			this.selectedRoles = [];
			this.roles = [];

			await this.fetchRoles();
		},

		removeSelectedRole(role) {
			this.selectedRoles = this.selectedRoles.filter(x => x.id !== role.id);
		},

		async handleSaveLinkUser() {
			try {
				await this.$services.user.linkCustomer({
					userId: this.userToLink.id,
					customerId: this.selectedCustomer.id,
					roleIds: this.selectedRoles.map(x => x.id)
				}, {
					loading: val => this.userLinkingIsLoading = val
				});

				this.userToLink = null;
				this.$refs.dataTable.fetchData();

			} catch (error) {
				this.error = error.message;
			}
		},

		getCustomersRecursively(treeNodes) {
			let customers = [];

			treeNodes.forEach(node => {
				customers.push({ name: node.name, id: node.id });

				if (node.children.length > 0)
					customers.push(...this.getCustomersRecursively(node.children));
			});

			return customers;
		},

		isItemExternal(item) {
			if (item?.customerId !== this.getActiveCustomer?.id)
				return true;

			return false;
		},

		async handleConfirmDeleteLinkedUser() {
			try {
				await this.$services.user.unlinkCustomer(this.userToLink.id, this.getActiveCustomer.id, null, {
					loading: val => this.userLinkingIsLoading = val
				});
			} catch (error) {
				this.userLinkingError = error.message;
			}

			this.displayDeleteLinkedUserModal = false;
			this.$refs.dataTable.fetchData();
		},

		handleCloseDeleteLinkedUserModal() {
			this.displayDeleteLinkedUserModal = false;
			this.userToLink = null;
		}
	},

	destroyed() {
		clearTimeout(this.companySearchTimeout);
		clearTimeout(this.customerSearchTimeout);
	}
};
</script>

<style lang="scss">
.link-user-modal {
	overflow: visible !important;

	& > div.modal-card {
		overflow: visible !important;
	}

	.select-container {
		label {
			color: white;
		}
	}
}

</style>
